import type { components } from '@core/types/clients/game-session'
import { useGameConfig, useI18n, useQuery, useXmppListener, useDayjs, type BingoRound, useClock, useGameSessionClient, type BingoLatestWinnersResponse, type BingoLatestWinner, useState, computed, useGameWinsClient, toGameWinner, isWinData, watch } from '#imports'

type WinData = components['schemas']['WinData']

export interface BingoRoundInformation {
    'bingo': BingoRound | null
    'bingo-90': BingoRound | null
}

const bingoGames: Array<keyof BingoRoundInformation> = ['bingo', 'bingo-90']

// game-session sends amounts as decimal strings; the lobby and the XMPP updates work in numbers
const toBingoRound = (slug: string, round: components['schemas']['NextBingoRoundData']): BingoRound => ({
    slug,
    nextRound: round.nextRound,
    megaPrizeAmount: Number(round.megaPrizeAmount),
    currency: round.currency,
    poolSize: Number(round.poolSize),
    currentRound: {
        slug,
        megaPrizeAmount: Number(round.currentRound.megaPrizeAmount),
        currency: round.currentRound.currency,
        poolSize: Number(round.currentRound.poolSize),
    },
})

export const useBingo = () => {
    const { bingo, assetsSiteFolder } = useGameConfig()
    const { t } = useI18n()
    const { clock } = useClock()
    const dayjs = useDayjs()
    const { listWins } = useGameWinsClient()
    const gameSessionClient = useGameSessionClient()

    const display = useState<{ 'bingo': string, 'bingo-90': string }>('next-round-display', () => ({ 'bingo': '', 'bingo-90': '' }))
    const nextRoundData = useState<BingoRoundInformation>('next-bingo-round-data', () => ({ 'bingo': null, 'bingo-90': null }))
    const latestBingoWinners = useState<Omit<BingoLatestWinnersResponse, 'success'>>('lastest-bingo-winners', () => ({ megaPrizeWinners: [], winners: [] }))

    function prependWinner(winners: BingoLatestWinner[] | undefined, win: WinData, limit: number) {
        if (winners?.some(({ id }) => id === win.id)) return winners

        return [toGameWinner(win, assetsSiteFolder), ...(winners?.slice(0, limit - 1) ?? [])]
    }

    function setupListeners() {
        if (import.meta.server) return
        // Bingo round updates (poolSize, next round etc.)
        useXmppListener<Omit<BingoRound, 'success' | 'currentRound'> | undefined>({ kind: 'message', xmlns: 'cego:BingoRoundUpdate' }, (result) => {
            if (!result) return
            nextRoundData.value[result.slug as keyof BingoRoundInformation] = result

            // Set display time of next round when xmpp data arrives
            setDisplayTimes(result.slug as keyof BingoRoundInformation)
        })

        // Latest bingo winners
        useXmppListener<unknown>({ kind: 'message', xmlns: 'cego:BingoWinUpdated' }, (win) => {
            if (!isWinData(win)) return

            latestBingoWinners.value.winners = prependWinner(latestBingoWinners.value.winners, win, bingo.max_winners_limit)
        })

        useXmppListener<unknown>({ kind: 'message', xmlns: 'cego:MegaPrizeWinUpdated' }, (win) => {
            if (!isWinData(win)) return

            latestBingoWinners.value.megaPrizeWinners = prependWinner(latestBingoWinners.value.megaPrizeWinners, win, bingo.mega_prize_winners_limit)
        })
    }

    const updateBingoWinners = (winnersData: Omit<BingoLatestWinnersResponse, 'success'>) => {
        latestBingoWinners.value.winners = winnersData.winners || []
        latestBingoWinners.value.megaPrizeWinners = winnersData.megaPrizeWinners || []
    }

    // The monolith sent row prizes and mega prizes as two lists; game-session has one, so ask twice by type.
    const getLatestBingoWinners = () => {
        const query = useQuery(async ({ signal }) => {
            const [winners, megaPrizeWinners] = await Promise.all([
                listWins({ types: ['one_row', 'two_rows', 'three_rows', 'four_rows', 'five_rows'], limit: bingo.max_winners_limit }, signal),
                listWins({ types: ['mega_prize'], limit: bingo.mega_prize_winners_limit }, signal),
            ])

            return { winners, megaPrizeWinners }
        }, { key: 'bingo:latest-winners', lazy: true, server: false })

        const bingoWinners = computed(() => ({
            winners: (query.data.value?.winners ?? []).map(win => toGameWinner(win, assetsSiteFolder)),
            megaPrizeWinners: (query.data.value?.megaPrizeWinners ?? []).map(win => toGameWinner(win, assetsSiteFolder)),
        }))

        watch(bingoWinners, updateBingoWinners)

        return query.enrich({ bingoWinners })
    }

    // Gets the next round data
    const getNextRound = (slug: keyof BingoRoundInformation) => {
        return useQuery(async ({ signal }) => {
            const response = await gameSessionClient.getNextBingoRound(slug, signal)
            if (response) {
                nextRoundData.value[slug] = toBingoRound(slug, response)

                setDisplayTimes(slug)
            }

            return response ?? null
        }, { key: `bingo:next-round:${slug}`, server: false, lazy: true })
    }

    const setDisplayTimes = (slug: keyof BingoRoundInformation | 'all' = 'all') => {
        if (slug === 'all') {
            // Compute all games display time
            bingoGames.forEach((gameSlug) => {
                display.value[gameSlug] = getEstimatedTimeToRoundStart(gameSlug)
            })
        }
        else {
            // Compute per slug passed
            display.value[slug] = getEstimatedTimeToRoundStart(slug)
        }
    }

    const getSecondsRemaining = (nextRound: string | null | undefined): number | null => {
        if (!nextRound) return null

        const opensAt = dayjs(nextRound).tz()

        if (!opensAt) return null

        const serverTime = dayjs(clock.value).tz()

        return opensAt.diff(serverTime, 'seconds')
    }

    const getEstimatedTimeToRoundStart = (slug: keyof BingoRoundInformation): string => {
        const nextRoundDate = nextRoundData.value[slug]?.nextRound
        const secondsRemaining: number | null = getSecondsRemaining(nextRoundDate)

        if (
            !secondsRemaining
            || secondsRemaining <= 0
            || secondsRemaining > 60
        ) return t('game.bingoPage.soon')

        if (secondsRemaining >= 30) return t('game.bingoPage.shortly')

        return t('game.bingoPage.now')
    }

    const getPrizePool = (slug: keyof BingoRoundInformation): number => {
        return nextRoundData.value[slug]?.poolSize ?? 0
    }

    const megaPrize = computed(() => nextRoundData.value['bingo']?.megaPrizeAmount || bingo.mega_prize)

    return {
        display,
        nextRoundData,
        latestBingoWinners,
        megaPrize,

        setupListeners,
        getNextRound,
        getPrizePool,
        getLatestBingoWinners,
        updateBingoWinners,
        setDisplayTimes,
    }
}
